import { Seo } from '../Seo'
import { PageHeader } from '../common/PageHeader'
import { Icon } from '../common/Icon'
import { Reveal } from '../animation/Reveal'
import { EnquiryForm } from './EnquiryForm'
import { breadcrumbSchema, faqSchema } from '../../lib/seo'
import { faqsInGroup } from '../../data/faq'

type Kind = 'retailer' | 'distributor'

const CONTENT: Record<
  Kind,
  { path: string; label: string; title: string; subtitle: string; seoDesc: string; points: { icon: string; title: string; body: string }[] }
> = {
  retailer: {
    path: '/retailer-enquiry',
    label: 'Become a retailer',
    title: 'Stock GreenbayBio at your counter.',
    subtitle: 'Bring a complete, bio-based input range to the farmers you serve — backed by crop-specific guidance.',
    seoDesc:
      'Become a GreenbayBio retailer. Offer farmers bio-fertilizers, biostimulants and plant-strength solutions, backed by crop-specific agronomic guidance.',
    points: [
      { icon: 'Package', title: 'A complete range', body: 'Ten bio-based products across five categories — soil biology to water management.' },
      { icon: 'Sprout', title: 'Crop-specific guidance', body: 'Clear, verified programmes make it easy to recommend the right product for each crop.' },
      { icon: 'ShieldCheck', title: 'Products you can trust', body: 'Full compositions, specifications and approvals published for every product.' },
      { icon: 'HeartHandshake', title: 'Partner support', body: 'Work directly with our team on product knowledge and demand in your area.' },
    ],
  },
  distributor: {
    path: '/distributor-enquiry',
    label: 'Become a distributor',
    title: 'Grow the GreenbayBio network.',
    subtitle: 'Partner with us to bring sustainable bio-innovation to farmers across your region.',
    seoDesc:
      'Become a GreenbayBio distributor. Partner with us to supply retailers and farmers across your region with sustainable bio-based crop inputs.',
    points: [
      { icon: 'MapPinned', title: 'Regional opportunity', body: 'Build and serve a retailer network across your territory.' },
      { icon: 'Layers', title: 'A differentiated portfolio', body: 'A focused, science-led range that stands apart from commodity inputs.' },
      { icon: 'FlaskConical', title: 'Backed by science', body: 'Global soil-biology, humic and botanical partners behind the products.' },
      { icon: 'Handshake', title: 'A real partnership', body: 'Work closely with our team on rollout, training and demand generation.' },
    ],
  },
}

export function PartnerEnquiry({ kind }: { kind: Kind }) {
  const c = CONTENT[kind]
  // The commercial questions, shared with /faq so there is one answer to "how do I become a retailer"
  // on the site rather than two that can drift.
  const faq = faqsInGroup('Buying & partnering').map((f) => ({ q: f.q, a: f.a }))
  return (
    <>
      <Seo
        title={`${c.label} — ${c.title}`}
        description={c.seoDesc}
        path={c.path}
        keywords={[`GreenbayBio ${kind}`, `become a ${kind}`, 'bio fertilizer distributor India']}
        jsonLd={[
          faqSchema(faq),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: c.label, path: c.path }]),
        ]}
      />
      <PageHeader
        eyebrow="Partner with us"
        title={c.title}
        subtitle={c.subtitle}
      />

      <div className="container-x grid gap-10 py-14 lg:grid-cols-[1fr_1fr] lg:gap-16">
        <div>
          <h2 className="font-display text-h2 font-semibold text-ink">Why partner with GreenbayBio</h2>
          <Reveal className="mt-6 grid gap-4 sm:grid-cols-2" stagger={0.1} y={20}>
            {c.points.map((p) => (
              <div key={p.title} className="rounded-xl border border-sand-200 bg-white p-6 shadow-soft">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                  <Icon name={p.icon} className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-display text-lg font-semibold text-ink">{p.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-500">{p.body}</p>
              </div>
            ))}
          </Reveal>
        </div>

        <div className="rounded-2xl border border-sand-200 bg-white p-7 shadow-soft md:p-9">
          <h2 className="font-display text-h3 font-semibold text-ink">{c.label}</h2>
          <p className="mt-1 text-sm text-ink-500">Share a few details and our team will reach out.</p>
          <div className="mt-6">
            <EnquiryForm defaultType={kind} lockType />
          </div>
        </div>
      </div>
    </>
  )
}
